import { useEffect } from "react";
import { useUserStore } from "@domains/auth/utils/useUserStore";
import {
  useSuspendLinkedAccountMutation,
  useConnectedLinkedAccountQuery,
  AwxLinkedAccountStatus,
} from "@common/utils/graphql";
import { FullPageLoader } from "@common/components/Loading/FullPageLoading";
import BankAccountItem from "@domains/settings/components/BankAccountCard";
import { Body, Heading } from "@hypercard/web-components";
import { useToast } from "@/components/ui/use-toast";
import { useUpdateLinkedAccountStatus } from "../hooks";

const ConnectedBankAccount = () => {
  const updatedLinkedAccountStatus = useUpdateLinkedAccountStatus();
  const { toast } = useToast();
  const userStore = useUserStore.getState();

  if (!userStore.workEmail) {
    alert("Please provide a work email");
  }

  // get bank details query here for admin view
  const { data: linkedAccountData, loading: linkedAccountLoading } =
    useConnectedLinkedAccountQuery({});
  const linkedAccount =
    linkedAccountData?.linkedAccount?.__typename === "QueryLinkedAccountSuccess"
      ? linkedAccountData.linkedAccount.data
      : null;
  // mutation to delete bank
  const [
    suspendLinkedAccountMutation,
    { data: suspendedLinkAccountData, loading: isDeleting },
  ] = useSuspendLinkedAccountMutation();

  useEffect(() => {
    if (
      suspendedLinkAccountData &&
      suspendedLinkAccountData.suspendLinkedAccount.__typename !==
        "MutationSuspendLinkedAccountSuccess"
    ) {
      toast({
        title: "Failed to delete bank account, please try again",
        variant: "destructive",
      });
    }
  }, [suspendedLinkAccountData, toast]);

  useEffect(() => {
    if (
      suspendedLinkAccountData?.suspendLinkedAccount.__typename ===
      "MutationSuspendLinkedAccountSuccess"
    ) {
      updatedLinkedAccountStatus(AwxLinkedAccountStatus.Suspended);
    }
  }, [suspendedLinkAccountData, isDeleting, updatedLinkedAccountStatus]);

  if (linkedAccountLoading && !linkedAccount) {
    return <FullPageLoader />;
  }

  const accountNumberLastFour =
    linkedAccount?.awxLinkedAccount?.usBank?.accountNumberLastFour;
  const linkedAccountStatus = linkedAccount?.awxLinkedAccount?.status ?? "";
  const institutionName = linkedAccount?.institutionName || "Unknown";
  const subtitle = accountNumberLastFour
    ? `****${accountNumberLastFour}`
    : linkedAccountStatus;

  return (
    <div className="ml-4 mr-4 mt-6 flex flex-grow flex-col">
      <div className="min-w-screen flex flex-col">
        <div className="start-0 flex flex-col items-start">
          <Heading size="lg" weight="regular" className="font-sans">
            Company Bank Account
          </Heading>
          <Body
            size="md"
            weight="regular"
            className="font-sans text-base text-slate"
          >
            All approved employee reimbursements will be withdrawn from this
            account and deposited into the employee's personal bank account.
            Please contact Hypercard support to cancel or reverse any transfers.
          </Body>
        </div>
        <div className="start-0 mt-8 flex flex-col items-start">
          <ul className="flex w-full flex-col gap-3">
            {linkedAccount && (
              <BankAccountItem
                key={
                  linkedAccount.awxLinkedAccount?.usBank
                    ?.accountNumberLastFour || "Unknown"
                }
                subtitle={subtitle}
                accountName={institutionName}
                isDeleting={isDeleting}
                onDelete={async () => await suspendLinkedAccountMutation()}
              />
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ConnectedBankAccount;
